import axios from 'axios'
import { getAuthStorage, clearAuthStorage } from './authStorage'

const API_KEY = process.env.VUE_APP_API_KEY
const apiBaseUrl = process.env.VUE_APP_API

// Instancia global de Axios con configuración base
export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
})
// ==========================================
// 8. MÓDULO DE MÁQUINAS
// ==========================================

/**
 * Obtiene el listado completo de máquinas activas.
 * Incluye: imagenes[], manuales[]
 */
export const getMaquinas = async () => {
  const response = await api.get('maquinas/')
  return response.data
}

/**
 * Crea una nueva máquina.
 * @param {{ nombre, marca, serie, area, criticidad }} data
 */
export const crearMaquina = async (data) => {
  const response = await api.post('maquinas/', data)
  return response.data
}

/**
 * Actualiza una máquina existente.
 * @param {number} id
 * @param {{ nombre, marca, serie, area, criticidad }} data
 */
export const actualizarMaquina = async (id, data) => {
  const response = await api.put(`maquinas/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de una máquina (activo = false).
 * @param {number} id
 */
export const eliminarMaquina = async (id) => {
  const response = await api.delete(`maquinas/${id}/`)
  return response.data
}

/**
 * Sube una imagen a una máquina específica.
 * @param {number} id - ID de la máquina
 * @param {File}   imagen
 */
export const agregarImagenMaquina = async (id, imagen) => {
  const formData = new FormData()
  formData.append('imagen', imagen)
  const response = await api.post(`maquinas/${id}/imagenes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

/**
 * Sube un manual (PDF) a una máquina específica.
 * @param {number} id - ID de la máquina
 * @param {File}   documento
 */
export const agregarManualMaquina = async (id, documento) => {
  const formData = new FormData()
  formData.append('documento', documento)
  const response = await api.post(`maquinas/${id}/manuales/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

// ==========================================
// 9. MÓDULO DE ACTIVIDADES
// ==========================================

/**
 * Obtiene el listado completo de actividades activas.
 */
export const getActividades = async () => {
  const response = await api.get('actividades/')
  return response.data
}

/**
 * Crea una nueva actividad.
 * @param {{ nombre, descripcion }} data
 */
export const crearActividad = async (data) => {
  const response = await api.post('actividades/', data)
  return response.data
}

/**
 * Actualiza una actividad existente.
 * @param {number} id
 * @param {{ nombre, descripcion }} data
 */
export const actualizarActividad = async (id, data) => {
  const response = await api.put(`actividades/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de una actividad.
 * @param {number} id
 */
export const eliminarActividad = async (id) => {
  const response = await api.delete(`actividades/${id}/`)
  return response.data
}

// ==========================================
// 10. MÓDULO DE REFACCIONES
// ==========================================

/**
 * Obtiene el listado de refacciones.
 * @param {{ en_almacen?: boolean }} params - Filtro opcional
 */
export const getRefacciones = async (params = {}) => {
  const response = await api.get('refacciones/', { params })
  return response.data
}

/**
 * Crea una nueva refacción.
 * Si en_almacen = false, cotizacion (archivo) es obligatoria.
 * @param {{ nombre_refaccion, proveedor, costo, en_almacen, cotizacion? }} data
 */
export const crearRefaccion = async (data) => {
  // Puede venir con archivo de cotización → multipart
  const isMultipart = data.cotizacion instanceof File
  const payload = isMultipart ? (() => {
    const fd = new FormData()
    Object.entries(data).forEach(([k, v]) => v !== undefined && fd.append(k, v))
    return fd
  })() : data

  const response = await api.post('refacciones/', payload, {
    headers: isMultipart ? { 'Content-Type': 'multipart/form-data' } : {}
  })
  return response.data
}

/**
 * Actualiza una refacción existente.
 * @param {number} id
 * @param {object} data
 */
export const actualizarRefaccion = async (id, data) => {
  const isMultipart = data.cotizacion instanceof File
  const payload = isMultipart ? (() => {
    const fd = new FormData()
    Object.entries(data).forEach(([k, v]) => v !== undefined && fd.append(k, v))
    return fd
  })() : data

  const response = await api.put(`refacciones/${id}/`, payload, {
    headers: isMultipart ? { 'Content-Type': 'multipart/form-data' } : {}
  })
  return response.data
}

/**
 * Borrado lógico de una refacción.
 * @param {number} id
 */
export const eliminarRefaccion = async (id) => {
  const response = await api.delete(`refacciones/${id}/`)
  return response.data
}

// ==========================================
// 11. MÓDULO DE RUTINAS
// ==========================================

/**
 * Obtiene el listado de rutinas.
 * @param {{ maquina?: number, tipo?: string }} params - Filtros opcionales
 * Incluye: rutina_actividades[], rutina_refacciones[], frecuencia_dias
 */
export const getRutinas = async (params = {}) => {
  const response = await api.get('rutinas/', { params })
  return response.data
}

/**
 * Obtiene una rutina individual con sus actividades y refacciones completas.
 * Útil para cargar rutina_refacciones al seleccionar un calendario en ejecución.
 * @param {number} id - ID de la rutina
 * @returns {{
 *   id, nombre, tipo, categoria, periodicidad,
 *   rutina_actividades: Array<{ id, actividad, orden, actividad_descripcion }>,
 *   rutina_refacciones: Array<{ id, refaccion, cantidad }>
 * }}
 */
export const getRutina = async (id) => {
  const response = await api.get(`rutinas/${id}/`)
  return response.data
}

/**
 * Crea una nueva rutina.
 * @param {{
 *   maquina: number,
 *   nombre: string,
 *   descripcion?: string,
 *   tipo: string,           // CONTROL | ELECTROMECANICO | HIDRAULICO | MECANICO | NEUMATICO
 *   categoria: string,      // PREVENTIVO | CORRECTIVO | PREDICTIVO
 *   periodicidad: string,   // SEMANAL | MENSUAL | TRIMESTRAL | SEMESTRAL | ANUAL
 *   duracion_estimada_horas: number
 * }} data
 */
export const crearRutina = async (data) => {
  const response = await api.post('rutinas/', data)
  return response.data
}

/**
 * Actualiza una rutina existente.
 * @param {number} id
 * @param {object} data
 */
export const actualizarRutina = async (id, data) => {
  const response = await api.put(`rutinas/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de una rutina.
 * @param {number} id
 */
export const eliminarRutina = async (id) => {
  const response = await api.delete(`rutinas/${id}/`)
  return response.data
}

/**
 * Agrega una actividad a una rutina.
 * @param {number} rutinaId
 * @param {{ actividad: number, orden: number }} data
 */
export const agregarActividadRutina = async (rutinaId, data) => {
  const response = await api.post(`rutinas/${rutinaId}/actividades/`, data)
  return response.data
}

/**
 * Elimina una actividad de una rutina.
 * @param {number} rutinaId
 * @param {number} actividadId - ID del RutinaActividad (no de Actividad)
 */
export const eliminarActividadRutina = async (rutinaId, actividadId) => {
  const response = await api.delete(`rutinas/${rutinaId}/actividades/${actividadId}/`)
  return response.data
}

/**
 * Agrega una refacción a una rutina.
 * @param {number} rutinaId
 * @param {{ refaccion: number, cantidad: number }} data
 */
export const agregarRefaccionRutina = async (rutinaId, data) => {
  const response = await api.post(`rutinas/${rutinaId}/refacciones/`, data)
  return response.data
}

/**
 * Elimina una refacción de una rutina.
 * @param {number} rutinaId
 * @param {number} refaccionId - ID del RutinaRefaccion (no de Refaccion)
 */
export const eliminarRefaccionRutina = async (rutinaId, refaccionId) => {
  const response = await api.delete(`rutinas/${rutinaId}/refacciones/${refaccionId}/`)
  return response.data
}

// ==========================================
// 12. MÓDULO DE CALENDARIO DE MANTENIMIENTO
// ==========================================

/**
 * Obtiene los eventos del calendario.
 * @param {{ maquina?: number, estatus?: string, mes?: string }} params
 *   mes en formato "YYYY-MM" (ej. "2025-06")
 * Incluye: actividades[] con su estado y fechas
 */
export const getCalendarios = async (params = {}) => {
  const response = await api.get('calendarios/', { params })
  return response.data
}

/**
 * Crea un nuevo evento en el calendario.
 * El backend auto-genera las CalendarioActividad desde la rutina.
 * @param {{
 *   maquina: number,
 *   rutina: number,
 *   fecha_programada: string   // YYYY-MM-DD
 * }} data
 */
export const crearCalendario = async (data) => {
  const response = await api.post('calendarios/', data)
  return response.data
}

/**
 * Actualiza un evento del calendario.
 * @param {number} id
 * @param {object} data
 */
export const actualizarCalendario = async (id, data) => {
  const response = await api.put(`calendarios/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de un evento del calendario.
 * @param {number} id
 */
export const eliminarCalendario = async (id) => {
  const response = await api.delete(`calendarios/${id}/`)
  return response.data
}

/**
 * Reprograma una actividad individual dentro de un evento del calendario.
 * @param {number} calendarioId
 * @param {number} actividadId  - ID del CalendarioActividad
 * @param {string} nuevaFecha   - YYYY-MM-DD
 */
export const reprogramarActividad = async (calendarioId, actividadId, nuevaFecha) => {
  const response = await api.patch(
    `calendarios/${calendarioId}/reprogramar-actividad/${actividadId}/`,
    { nueva_fecha: nuevaFecha }
  )
  return response.data
}

// ==========================================
// 13. MÓDULO DE EJECUCIÓN DE MANTENIMIENTO
// ==========================================

/**
 * Obtiene el listado de ejecuciones.
 * @param {{ maquina?: number, calendario?: number }} params - Filtros opcionales
 * Incluye: actividades[], refacciones[], imagenes[], duracion_real_horas
 */
export const getEjecuciones = async (params = {}) => {
  const response = await api.get('ejecuciones/', { params })
  return response.data
}

/**
 * Inicia una nueva ejecución de mantenimiento.
 * El backend auto-genera las EjecucionActividad desde la rutina.
 * @param {{
 *   calendario: number,
 *   maquina: number,
 *   rutina: number,
 *   fecha_inicio: string,            // ISO datetime
 *   tiempo_ejecucion_horas?: number,
 *   notas?: string
 * }} data
 */
export const crearEjecucion = async (data) => {
  const response = await api.post('ejecuciones/', data)
  return response.data
}

/**
 * Actualiza una ejecución (ej. registrar fecha_fin, notas).
 * @param {number} id
 * @param {object} data
 */
export const actualizarEjecucion = async (id, data) => {
  const response = await api.patch(`ejecuciones/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de una ejecución.
 * @param {number} id
 */
export const eliminarEjecucion = async (id) => {
  const response = await api.delete(`ejecuciones/${id}/`)
  return response.data
}

/**
 * Sube una imagen a una ejecución en curso.
 * @param {number} ejecucionId
 * @param {File}   imagen
 */
export const agregarImagenEjecucion = async (ejecucionId, imagen) => {
  const formData = new FormData()
  formData.append('imagen', imagen)
  const response = await api.post(`ejecuciones/${ejecucionId}/imagenes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

/**
 * Marca una actividad como completada (o agrega observaciones).
 * @param {number} ejecucionId
 * @param {number} actividadId  - ID del EjecucionActividad
 * @param {{ completado?: boolean, observaciones?: string }} data
 */
export const actualizarActividadEjecucion = async (ejecucionId, actividadId, data) => {
  const response = await api.patch(
    `ejecuciones/${ejecucionId}/actividades/${actividadId}/`,
    data
  )
  return response.data
}

/**
 * Registra el uso de una refacción durante la ejecución.
 * @param {number} ejecucionId
 * @param {{ refaccion: number, cantidad: number }} data
 */
export const agregarRefaccionEjecucion = async (ejecucionId, data) => {
  const response = await api.post(`ejecuciones/${ejecucionId}/refacciones/`, data)
  return response.data
}

export const actualizarRefaccionEjecucion = async (ejecucionId, refaccionId, data) => {
  const response = await api.patch(`ejecuciones/${ejecucionId}/refacciones/${refaccionId}/`, data)
  return response.data
}

export const eliminarRefaccionEjecucion = async (ejecucionId, refaccionId) => {
  const response = await api.delete(`ejecuciones/${ejecucionId}/refacciones/${refaccionId}/`)
  return response.data
}

export const eliminarImagenEjecucion = async (ejecucionId, imagenId) => {
  const response = await api.delete(`ejecuciones/${ejecucionId}/imagenes/${imagenId}/`)
  return response.data
}
/**
 * INTERCEPTOR DE PETICIONES
 * Se encarga de adjuntar automáticamente el JWT (JSON Web Token) en el header
 * de todas las peticiones salientes hacia el backend de AWS.
 */
//X-Api-Key va SIEMPRE, Authorization solo en privados
// INTERCEPTOR DE PETICIONES
api.interceptors.request.use((config) => {
    const url = config.url || ''
    const isPublicEndpoint = url.includes('public/')

    // X-Api-Key SOLO en endpoints públicos
    if (API_KEY && isPublicEndpoint) {
      config.headers['x-api-key'] = API_KEY
    }

    // Authorization SOLO en endpoints privados
    const token = getAuthStorage().getItem('access_token')
    if (token && !isPublicEndpoint) {
        config.headers.Authorization = `Bearer ${token}`
    }

    return config
}, (error) => {
    return Promise.reject(error)
})

// ── Renovación silenciosa del access token ────────────────────────
// El backend tiene ROTATE_REFRESH_TOKENS activo: cada refresh emite un
// refresh token nuevo e invalida el anterior, así que hay que persistir
// ambos. Se usa `axios` "crudo" (no `api`) para no volver a disparar
// estos mismos interceptores si el refresh también falla.
let isRefreshing = false
let colaPendientes = []

function resolverPendientes(nuevoToken) {
  colaPendientes.forEach((cb) => cb(nuevoToken))
  colaPendientes = []
}

async function refrescarAccessToken() {
  const storage = getAuthStorage()
  const refresh = storage.getItem('refresh_token')
  if (!refresh) throw new Error('Sin refresh token disponible')

  const { data } = await axios.post(`${apiBaseUrl}auth/token/refresh/`, { refresh })

  storage.setItem('access_token', data.access)
  if (data.refresh) storage.setItem('refresh_token', data.refresh)

  return data.access
}

// INTERCEPTOR DE RESPUESTA
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error
    const status = response?.status
    const url = config?.url || ''
    const isPublicEndpoint = url.includes('public/')
    const hadToken = !!getAuthStorage().getItem('access_token')

    if (status === 401 && !isPublicEndpoint && hadToken && !config._retriedAfterRefresh) {
      config._retriedAfterRefresh = true

      // Ya hay una renovación en curso: esperar su resultado en vez de
      // disparar varios refresh en paralelo por cada petición 401.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          colaPendientes.push((nuevoToken) => {
            if (!nuevoToken) return reject(error)
            config.headers.Authorization = `Bearer ${nuevoToken}`
            resolve(api(config))
          })
        })
      }

      isRefreshing = true
      try {
        const nuevoToken = await refrescarAccessToken()
        isRefreshing = false
        resolverPendientes(nuevoToken)
        config.headers.Authorization = `Bearer ${nuevoToken}`
        return api(config)
      } catch (refreshError) {
        isRefreshing = false
        resolverPendientes(null)
        console.error('No se pudo renovar la sesión. Redirigiendo a login...')
        clearAuthStorage()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// ==========================================
// 1. AUTENTICACIÓN
// ==========================================

/**
 * Login para el Administrador
 * @param {string} email 
 * @param {string} password 
 */
export const loginAdmin = async (email, password) => {
  try {
    const response = await api.post('auth/login/', {
      email: email,
      password: password
    })

    return response.data
  } catch (error) {
    console.error('Error en loginAdmin:', error)
    throw error
  }
}

/**
 * Invalida el refresh token actual en el backend (blacklist) al cerrar
 * sesión manualmente. Best-effort: si falla (backend caído, token ya
 * vencido, etc.) no debe impedir que el logout local continúe.
 * @param {string} refreshToken
 */
export const logoutAdmin = async (refreshToken) => {
  if (!refreshToken) return
  try {
    await api.post('auth/logout/', { refresh: refreshToken })
  } catch (error) {
    console.error('No se pudo invalidar la sesión en el servidor:', error)
  }
}

// ==========================================
// 2. MÓDULO DE INVENTARIO (Divisan)
// ==========================================

export const getProductoPorCodigo = async (codigo) => {
  // Endpoint público (vista del QR): el interceptor manda X-Api-Key por el prefijo public/
  const response = await api.get(`public/productos/${encodeURIComponent(codigo)}/`)
  return response.data
}

export const getInventarioCompleto = async () => {
  const response = await api.get('inventario/')
  return response.data
}

// Igual que getInventarioCompleto pero incluye productos dados de baja
// (activo=False). Solo lo usa el catálogo de Productos, que muestra la
// columna "Activo"; el resto de selectores debe seguir viendo solo activos.
export const getCatalogoProductosCompleto = async () => {
  const response = await api.get('inventario/', { params: { todos: '1' } })
  return response.data
}

export const crearProducto = async (data) => {
  const response = await api.post('inventario/', data)
  return response.data
}

export const actualizarProducto = async (codigo, data) => {
  const response = await api.patch(`inventario/${codigo}/`, data)
  return response.data
}

export const eliminarProducto = async (codigo) => {
  const response = await api.delete(`inventario/${codigo}/`)
  return response.data
}

// Borrador libre de inventario inicial: editable cuantas veces haga falta
// mientras el motor de inventarios no esté activado.
export const guardarInventarioInicialBorrador = async (codigo, cantidad) => {
  const response = await api.patch(`inventario/${codigo}/inventario-inicial/`, { cantidad })
  return response.data
}

export const registrarAjusteInventario = async (codigo, { cantidad, positivo, motivo, observaciones, fecha }) => {
  const response = await api.post(`inventario/${codigo}/ajuste/`, { cantidad, positivo, motivo, observaciones, fecha })
  return response.data
}

export const getConfiguracionInventario = async () => {
  const response = await api.get('inventario/configuracion/')
  return response.data
}

export const activarMotorInventario = async (fechaCorte) => {
  const response = await api.patch('inventario/configuracion/', { fecha_corte: fechaCorte })
  return response.data
}

export const exportarProductos = async (search = '') => {
  const response = await api.get('exportar/productos/', {
    params: search ? { search } : {},
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'catalogo_productos.xlsx')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// Endpoint para eliminar un mantenimiento específico
export const eliminarMantenimiento = async (id) => {
  const response = await api.delete(`mantenimientos/${id}/`)
  return response.data
}

// ==========================================
// 3. MÓDULO DE MOLDES Y PROCESOS (Divisan)
// ==========================================

/**
 * CRUD DE MOLDES
 * Gestiona los activos físicos de la planta (Engorda, Mega, Tradicional, etc.)
 */
export const getMoldes = async () => {
  const response = await api.get('moldes/')
  return response.data
}

export const crearMolde = async (data) => {
  const response = await api.post('moldes/', data)
  return response.data
}

export const actualizarMolde = async (id, data) => {
  const response = await api.put(`moldes/${id}/`, data)
  return response.data
}

export const eliminarMolde = async (id) => {
  const response = await api.delete(`moldes/${id}/`)
  return response.data
}

/**
 * CRUD DE TIPOS DE EVENTO
 * Categorías: Producción (PROD), Mantenimiento (MTTO), Refacción (REFA)
 */
export const getTiposEvento = async () => {
  const response = await api.get('tipos-evento/')
  return response.data
}

/**
 * Crea una nueva categoría de evento
 */
export const crearTipoEvento = async (data) => {
  const response = await api.post('tipos-evento/', data)
  return response.data
}

/**
 * Actualiza una categoría existente
 */
export const actualizarTipoEvento = async (id, data) => {
  const response = await api.put(`tipos-evento/${id}/`, data)
  return response.data
}

/**
 * Realiza el borrado lógico de una categoría
 */
export const eliminarTipoEvento = async (id) => {
  const response = await api.delete(`tipos-evento/${id}/`)
  return response.data
}

/**
 * GESTIÓN DE MANTENIMIENTOS (HISTORIAL)
 * Maneja los registros de intervención de moldes y carga de imágenes.
 */
export const getMantenimientos = async (params = {}) => {
  const response = await api.get('mantenimientos/', { params })
  return response.data
}

export const crearMantenimiento = async (formData) => {
  const response = await api.post('mantenimientos/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return response.data
}

/** 
 * Endpoint para obtener el ultimo mantenimiento de un molde específico
 */ 
export const getMoldeUltimoMantenimiento = async (codigoMolde) => {
  const response = await api.get(`public/moldes/${codigoMolde}/`)
  return response.data
}
// ==========================================
// MÓDULO DE CLIENTES
// ==========================================

/**
 * Obtiene el listado completo de clientes.
 * Ahora incluye el campo `telefono`.
 */
export const getClientes = async () => {
  const response = await api.get('clientes/')
  return response.data
}

/**
 * Crea un nuevo cliente.
 * @param {{ numero_cliente: string, nombre: string, telefono?: string }} data
 */
export const crearCliente = async (data) => {
  const response = await api.post('clientes/', data)
  return response.data
}

/**
 * Actualiza un cliente existente (PUT completo).
 * @param {number} id
 * @param {{ numero_cliente: string, nombre: string, telefono?: string }} data
 */
export const actualizarCliente = async (id, data) => {
  const response = await api.put(`clientes/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de un cliente (activo = false).
 * @param {number} id
 */
export const eliminarCliente = async (id) => {
  const response = await api.delete(`clientes/${id}/`)
  return response.data
}

// ==========================================
// 5. MÓDULO DE SERVICIOS Y CATÁLOGO DE PEDIDOS
// ==========================================
// El antiguo catálogo de Artículos de Ventas se dividió en Productos
// (inventario, los mantiene CP&L) y Servicios (envío, impresiones extra; los
// mantiene Gestión de pedidos).

/**
 * Productos + servicios en una sola lista para armar pedidos.
 * Cada entrada: { id: "P:<código>" | "S:<código>", tipo, codigo, descripcion }
 */
export const getCatalogoPedido = async () => {
  const response = await api.get('pedidos/catalogo/')
  return response.data
}

/**
 * Convierte la clave del catálogo de pedidos al campo que espera el backend.
 * @param {string} clave "P:<código>" o "S:<código>"
 */
export const renglonDesdeClave = (clave) => {
  const [tipo, ...resto] = String(clave).split(':')
  const codigo = resto.join(':')
  return tipo === 'S' ? { servicio: codigo } : { producto: codigo }
}

export const esClaveServicio = (clave) => String(clave || '').startsWith('S:')

export const getServicios = async () => {
  const response = await api.get('servicios/')
  return response.data
}

export const crearServicio = async (data) => {
  const response = await api.post('servicios/', data)
  return response.data
}

export const actualizarServicio = async (id, data) => {
  const response = await api.put(`servicios/${id}/`, data)
  return response.data
}

export const eliminarServicio = async (id) => {
  const response = await api.delete(`servicios/${id}/`)
  return response.data
}
// ==========================================
// 6. MÓDULO DE PEDIDOS
// ==========================================

/**
 * Obtiene todos los pedidos activos
 * El serializer incluye: cliente_nombre, estatus_cliente, dias_restantes, fecha_limite, items[]
 */
export const getPedidos = async () => {
  const response = await api.get('pedidos/')
  return response.data
}

/**
 * Crea un pedido con sus ítems en una sola llamada
 * @param {{
 *   numero_pedido: number,
 *   cliente: number,
 *   fecha_recibo: string,       // YYYY-MM-DD
 *   total: number,
 *   prioridad: string,          // alta | media | baja
 *   notas?: string,
 *   items: Array<{ producto?: string, servicio?: string, cantidad: number, notas?: string }>
 * }} data
 */
export const crearPedido = async (data) => {
  const response = await api.post('pedidos/', data)
  return response.data
}

/**
 * Actualiza parcialmente un pedido (PATCH)
 * Útil para cambiar estatus_almacen, guia, fecha_entrega_real, prioridad, etc.
 * @param {number} id
 * @param {object} data - Solo los campos a modificar
 */
export const actualizarPedido = async (id, data) => {
  const response = await api.patch(`pedidos/${id}/`, data)
  return response.data
}

/**
 * Actualiza los campos de seguimiento de un pedido (Brenda)
 * Solo acepta: fecha_compromiso, estatus_almacen, notas
 * @param {number} numeroPedido
 * @param {object} data
 */
export const actualizarSeguimientoPedido = async (numeroPedido, data) => {
  const response = await api.patch(`pedidos/${numeroPedido}/seguimiento/`, data)
  return response.data
}

/**
 * Elimina físicamente un pedido moviéndolo a PedidoEliminado
 * @param {number} numeroPedido
 * @param {string} motivo - obligatorio
 */
export const eliminarPedido = async (numeroPedido, motivo) => {
  const response = await api.post(`pedidos/${numeroPedido}/eliminar/`, { motivo })
  return response.data
}

/**
 * Cancela un pedido (permanece en BD con estatus Cancelado)
 * @param {number} numeroPedido
 * @param {string} motivo - obligatorio
 */
export const cancelarPedido = async (numeroPedido, motivo) => {
  const response = await api.patch(`pedidos/${numeroPedido}/cancelar/`, { motivo })
  return response.data
}

// ==========================================
// 7. MÓDULO DE ÍTEMS DE PEDIDO
// ==========================================

export const getPedidoItems = async (pedidoId) => {
  const response = await api.get('pedido-items/', { params: { pedido: pedidoId } })
  return response.data
}

export const crearPedidoItem = async (data) => {
  const response = await api.post('pedido-items/', data)
  return response.data
}

export const actualizarPedidoItem = async (id, data) => {
  const response = await api.patch(`pedido-items/${id}/`, data)
  return response.data
}

export const eliminarPedidoItem = async (id) => {
  const response = await api.delete(`pedido-items/${id}/`)
  return response.data
}

export const analizarPdfPedido = async (pdfFile) => {
  const formData = new FormData();
  formData.append('pdf', pdfFile);

  const response = await api.post('analizar-pdf/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * Descarga un Excel con los pedidos en el rango de fecha_recibo
 * @param {string} fechaInicio - YYYY-MM-DD
 * @param {string} fechaFin    - YYYY-MM-DD
 */
export const exportarPedidos = async (fechaInicio, fechaFin) => {
  const response = await api.get("pedidos/exportar/", {
    params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin },
    responseType: "blob",   // ← clave para recibir el archivo binario
  });

  // Crear link de descarga y dispararlo automáticamente
  const url  = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", `pedidos_${fechaInicio}_a_${fechaFin}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Descarga un Excel de seguimiento de pedidos (una fila por pedido, sin ítems)
 * @param {string} fechaInicio - YYYY-MM-DD
 * @param {string} fechaFin    - YYYY-MM-DD
 */
export const exportarSeguimientoPedidos = async (fechaInicio, fechaFin) => {
  const response = await api.get("pedidos/exportar-seguimiento/", {
    params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin },
    responseType: "blob",
  });

  const url  = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", `seguimiento_pedidos_${fechaInicio}_a_${fechaFin}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const exportarClientes = async () => {
  const response = await api.get('clientes/exportar/', { responseType: 'blob' })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'clientes.xlsx')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const getCorrectivos = async (params = {}) => {
  const response = await api.get('correctivos/', { params })
  return response.data
}
 
export const getCorrectivo = async (id) => {
  const response = await api.get(`correctivos/${id}/`)
  return response.data
}
 
/**
 * Crea un nuevo registro de mantenimiento correctivo.
 * Las refacciones e imágenes se agregan después con endpoints separados.
 * @param {{
 *   maquina: number,
 *   fecha: string,           // YYYY-MM-DD
 *   falla: string,
 *   reparacion: string,
 *   operador: string,
 *   tecnico: string,
 *   tiempo_valor: number,
 *   tiempo_unidad: string    // MINUTOS | HORAS | DIAS
 * }} data
 */
export const crearCorrectivo = async (data) => {
  const response = await api.post('correctivos/', data)
  return response.data
}
 
export const actualizarCorrectivo = async (id, data) => {
  const response = await api.patch(`correctivos/${id}/`, data)
  return response.data
}
 
export const eliminarCorrectivo = async (id) => {
  const response = await api.delete(`correctivos/${id}/`)
  return response.data
}
 
// ── Refacciones del correctivo ────────────────────────────────
 
/**
 * Agrega una refacción utilizada en la intervención.
 * @param {number} correctivoId
 * @param {{ refaccion: number, cantidad: number }} data
 */
export const agregarRefaccionCorrectivo = async (correctivoId, data) => {
  const response = await api.post(`correctivos/${correctivoId}/refacciones/`, data)
  return response.data
}
 
export const eliminarRefaccionCorrectivo = async (correctivoId, refId) => {
  const response = await api.delete(`correctivos/${correctivoId}/refacciones/${refId}/`)
  return response.data
}
 
// ── Imágenes del correctivo ───────────────────────────────────
 
/**
 * Sube una imagen de evidencia al correctivo.
 * @param {number} correctivoId
 * @param {File}   imagen
 */
export const agregarImagenCorrectivo = async (correctivoId, imagen) => {
  const formData = new FormData()
  formData.append('imagen', imagen)
  const response = await api.post(
    `correctivos/${correctivoId}/imagenes/`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}
 
export const eliminarImagenCorrectivo = async (correctivoId, imgId) => {
  const response = await api.delete(`correctivos/${correctivoId}/imagenes/${imgId}/`)
  return response.data
}

/**
 * Sube o reemplaza la factura PDF de un pedido.
 * @param {number} numeroPedido
 * @param {File}   pdfFile
 * @returns {{ factura_pdf: string }}  URL del archivo en S3
 */
export const subirFacturaPedido = async (numeroPedido, pdfFile) => {
  const formData = new FormData()
  formData.append('factura_pdf', pdfFile)
  const response = await api.patch(`pedidos/${numeroPedido}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/**
 * Encola la tarea Celery que manda la plantilla de envío por WhatsApp.
 * @param {number} numeroPedido
 * @returns {{ detail: string, task_id: string }}
 */
export const notificarEnvioPedido = async (numeroPedido) => {
  const response = await api.post(`pedidos/${numeroPedido}/notificar-envio/`)
  return response.data
}

/**
 * Encola la tarea Celery que manda la factura PDF por WhatsApp.
 * @param {number} numeroPedido
 * @returns {{ detail: string, task_id: string }}
 */
export const enviarFacturaPedido = async (numeroPedido) => {
  const response = await api.post(`pedidos/${numeroPedido}/enviar-factura/`)
  return response.data
}

/**
 * Consulta el resultado de una tarea Celery en el result backend.
 * Usar en polling hasta que estado !== "pending".
 * @param {string} taskId
 * @returns {{
 *   estado: "pending" | "success" | "failure",
 *   ok?: boolean,
 *   wamid?: string,
 *   error?: string
 * }}
 */
export const consultarTaskStatus = async (taskId) => {
  const response = await api.get(`pedidos/task-status/${taskId}/`)
  return response.data
}

/**
 * Histórico de mensajes de WhatsApp de un pedido, con su estado real
 * (el que reporta Meta por webhook, no solo el "aceptado" del envío).
 *
 * Se usa al abrir el modal para pintar el estado actual; los cambios
 * posteriores llegan por WebSocket (ws_whatsapp.js).
 *
 * @param {number} numeroPedido
 * @returns {Array<{
 *   id: number,
 *   wamid: string|null,
 *   tipo: "FACTURA" | "GUIAS" | "REPORTE",
 *   estatus: "PENDIENTE"|"ACEPTADO"|"RECHAZADO"|"ENVIADO"|"ENTREGADO"|"LEIDO"|"FALLIDO",
 *   telefono: string,
 *   fecha_enviado: string|null,
 *   fecha_entregado: string|null,
 *   fecha_leido: string|null,
 *   fecha_fallido: string|null,
 *   error_codigo: string,
 *   error_titulo: string,
 *   error_detalle: string,
 *   enviado_por_nombre: string|null,
 *   fecha_creacion: string
 * }>}
 */
// ==========================================
// PREAPROBACIÓN DE DISEÑOS (Ventas → Diseños)
// ==========================================

/**
 * Listado de preaprobaciones. `buscar` filtra por cotización o cliente.
 * @param {string} [buscar]
 */
export const getDisenos = async (buscar = "") => {
  const response = await api.get("disenos/", {
    params: buscar ? { buscar } : {},
  })
  return response.data?.results ?? response.data
}

/**
 * Crea una preaprobación con sus PDFs (máx. 15).
 * @param {{ cotizacion: string, clienteId: number, archivos: File[], ventaPublico?: boolean, contactoNombre?: string, contactoEstado?: string, contactoTelefono?: string }} datos
 */
export const crearDiseno = async ({
  cotizacion, clienteId, archivos,
  ventaPublico, contactoNombre, contactoEstado, contactoTelefono,
}) => {
  const formData = new FormData()
  formData.append("cotizacion", cotizacion)
  formData.append("venta_publico", !!ventaPublico)
  if (ventaPublico) {
    formData.append("contacto_nombre", contactoNombre)
    formData.append("contacto_estado", contactoEstado || "")
    formData.append("contacto_telefono", contactoTelefono)
  } else {
    formData.append("cliente_id", clienteId)
  }
  archivos.forEach((archivo) => formData.append("archivos", archivo))

  const response = await api.post("disenos/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return response.data
}

/**
 * Edita una preaprobación no notificada.
 * Si se mandan `archivos`, reemplazan por completo los PDFs actuales.
 * @param {number} id
 * @param {{ cotizacion?: string, clienteId?: number, archivos?: File[], ventaPublico?: boolean, contactoNombre?: string, contactoEstado?: string, contactoTelefono?: string }} datos
 */
export const actualizarDiseno = async (id, {
  cotizacion, clienteId, archivos,
  ventaPublico, contactoNombre, contactoEstado, contactoTelefono,
}) => {
  const formData = new FormData()
  if (cotizacion !== undefined) formData.append("cotizacion", cotizacion)
  formData.append("venta_publico", !!ventaPublico)
  if (ventaPublico) {
    formData.append("contacto_nombre", contactoNombre)
    formData.append("contacto_estado", contactoEstado || "")
    formData.append("contacto_telefono", contactoTelefono)
  } else if (clienteId !== undefined) {
    formData.append("cliente_id", clienteId)
  }
  if (archivos?.length) archivos.forEach((a) => formData.append("archivos", a))

  const response = await api.patch(`disenos/${id}/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return response.data
}

export const eliminarDiseno = async (id) => {
  await api.delete(`disenos/${id}/`)
}

/**
 * Quita un PDF concreto de una preaprobación no notificada.
 * @param {number} id     id de la preaprobación
 * @param {number} pdfId  id del documento
 */
export const eliminarPdfDiseno = async (id, pdfId) => {
  await api.delete(`disenos/${id}/pdfs/${pdfId}/`)
}

/**
 * Encola el WhatsApp con el link de revisión para el cliente.
 * @returns {{ detail: string, task_id: string }}
 */
export const notificarDiseno = async (id) => {
  const response = await api.post(`disenos/${id}/notificar/`)
  return response.data
}

/** Descarga el Excel: un renglón por documento, no por registro. */
export const exportarDisenos = async () => {
  const response = await api.get("disenos/exportar/", { responseType: "blob" })

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", "disenos.xlsx")
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ── Endpoints públicos (los usa la página del cliente, sin sesión) ──────────
// Van con axios "pelón" en vez de la instancia `api` porque esa inyecta el
// token y, ante un 401, dispara el flujo de logout. Aquí no hay sesión.

const publicBaseUrl = apiBaseUrl

/** @param {string} token UUID de la preaprobación */
export const getDisenoPublico = async (token) => {
  const response = await axios.get(`${publicBaseUrl}publico/disenos/${token}/`)
  return response.data
}

/**
 * Responde un PDF desde la página pública.
 * @param {string} token
 * @param {number} pdfId
 * @param {{ accion: "aprobar"|"rechazar", comentario?: string }} respuesta
 */
export const responderDisenoPublico = async (token, pdfId, respuesta) => {
  const response = await axios.post(
    `${publicBaseUrl}publico/disenos/${token}/pdfs/${pdfId}/responder/`,
    respuesta
  )
  return response.data
}

export const getMensajesWhatsapp = async (numeroPedido) => {
  const response = await api.get(`whatsapp/mensajes/`, {
    params: { pedido: numeroPedido },
  })
  // ListAPIView devuelve arreglo plano o {results} si hay paginación global
  return response.data?.results ?? response.data
}


export const eliminarManualMaquina = async (maquinaId, manualId) => {
  await api.delete(`maquinas/${maquinaId}/manuales/${manualId}/`)
}
export const eliminarImagenMaquina = async (maquinaId, imagenId) => {
  await api.delete(`maquinas/${maquinaId}/imagenes/${imagenId}/`)
}

// ==========================================
// MÓDULO DE PAQUETERÍAS
// ==========================================

/**
 * Obtiene el listado de paqueterías activas.
 * @returns {Array<{ id, nombre, activo, fecha_creacion }>}
 */
export const getPaqueterias = async () => {
  const response = await api.get('paqueterias/')
  return response.data
}

/**
 * Crea una nueva paquetería.
 * @param {{ nombre: string }} data
 */
export const crearPaqueteria = async (data) => {
  const response = await api.post('paqueterias/', data)
  return response.data
}

/**
 * Actualiza una paquetería.
 * @param {number} id
 * @param {{ nombre: string }} data
 */
export const actualizarPaqueteria = async (id, data) => {
  const response = await api.put(`paqueterias/${id}/`, data)
  return response.data
}

/**
 * Soft-delete de una paquetería (activo = false).
 * @param {number} id
 */
export const eliminarPaqueteria = async (id) => {
  const response = await api.delete(`paqueterias/${id}/`)
  return response.data
}

// ==========================================
// MÓDULO DE GUÍAS (nested bajo pedido)
// ==========================================

/**
 * Agrega una guía de envío a un pedido.
 * @param {number} numeroPedido
 * @param {{ paqueteria_id: number, numero_guia: string }} data
 * @returns {{ id, paqueteria_nombre, numero_guia, fecha_registro }}
 */
export const agregarGuiaPedido = async (numeroPedido, data) => {
  const response = await api.post(`pedidos/${numeroPedido}/guias/`, data)
  return response.data
}

/**
 * Elimina una guía de envío de un pedido.
 * @param {number} numeroPedido
 * @param {number} guiaId
 */
export const eliminarGuiaPedido = async (numeroPedido, guiaId) => {
  await api.delete(`pedidos/${numeroPedido}/guias/${guiaId}/`)
}

export async function reportarPedidos() {
  const response = await api.post(`/pedidos/reporte-pedidos/`)
  return response.data
}

// ==========================================
// MÓDULO INVENTARIO DE ACTIVOS
// ==========================================
// ── Categorías ─────────────────────────────
export const getCategoriasActivo = async () => {
  const response = await api.get('categorias/')
  return response.data
}

export const crearCategoriaActivo = async (data) => {
  const response = await api.post('categorias/', data)
  return response.data
}

export const actualizarCategoriaActivo = async (id, data) => {
  const response = await api.put(`categorias/${id}/`, data)
  return response.data
}

export const eliminarCategoriaActivo = async (id) => {
  const response = await api.delete(`categorias/${id}/`)
  return response.data
}

// ── Activos Fijos ───────────────────────────
export const getActivosFijos = async (params = {}) => {
  const response = await api.get('activos/', { params })
  return response.data
}

export const getActivoFijo = async (id) => {
  const response = await api.get(`activos/${id}/`)
  return response.data
}

export const crearActivoFijo = async (data) => {
  const response = await api.post('activos/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

export const actualizarActivoFijo = async (id, data) => {
  const response = await api.patch(`activos/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

export const eliminarActivoFijo = async (id, motivo) => {
  const response = await api.delete(`activos/${id}/`, {
    data: { motivo }
  })
  return response.data
}

export const subirImagenActivoFijo = (activoId, formData) =>
  api.post(`activos/${activoId}/imagenes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data)

export const eliminarImagenActivoFijo = (imagenId) =>
  api.delete(`activos/imagenes/${imagenId}/`).then(r => r.data)

// ── Categorías de actividad (módulo máquinas) ─────────────
export const getCategoriasActividad = async () => {
  const response = await api.get('categorias-actividad/')
  return response.data
}

export const crearCategoriaActividad = async (data) => {
  const response = await api.post('categorias-actividad/', data)
  return response.data
}

export const actualizarCategoriaActividad = async (id, data) => {
  const response = await api.put(`categorias-actividad/${id}/`, data)
  return response.data
}

export const eliminarCategoriaActividad = async (id) => {
  const response = await api.delete(`categorias-actividad/${id}/`)
  return response.data
}

// ── Áreas de Trabajo (módulo rutinas de mantenimiento) ─────────
/**
 * Obtiene el listado de áreas de trabajo activas.
 * @returns {Array<{ id, nombre, descripcion, activo }>}
 */
export const getAreasTrabajo = async () => {
  const response = await api.get('areas-trabajo/')
  return response.data
}

/**
 * Crea una nueva área de trabajo.
 * @param {{ nombre: string, descripcion?: string }} data
 */
export const crearAreaTrabajo = async (data) => {
  const response = await api.post('areas-trabajo/', data)
  return response.data
}

/**
 * Actualiza un área de trabajo existente.
 * @param {number} id
 * @param {{ nombre: string, descripcion?: string }} data
 */
export const actualizarAreaTrabajo = async (id, data) => {
  const response = await api.put(`areas-trabajo/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de un área de trabajo (activo = false).
 * @param {number} id
 */
export const eliminarAreaTrabajo = async (id) => {
  const response = await api.delete(`areas-trabajo/${id}/`)
  return response.data
}

export const exportarActivosFijos = async () => {
  const response = await api.get('activos/exportar/', {
    responseType: 'blob'
  })
  const url  = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href  = url
  link.setAttribute('download', 'activos_fijos.xlsx')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ── Áreas de Empresa ────────────────────────
/**
 * Obtiene el listado de áreas activas.
 * @returns {Array<{ id, nombre, descripcion, activo, fecha_creacion, fecha_modificacion }>}
 */
export const getAreasEmpresa = async () => {
  const response = await api.get('areas/')
  return response.data
}

/**
 * Crea una nueva área de empresa.
 * @param {{ nombre: string, descripcion?: string }} data
 */
export const crearAreaEmpresa = async (data) => {
  const response = await api.post('areas/', data)
  return response.data
}

/**
 * Actualiza un área existente.
 * @param {number} id
 * @param {{ nombre: string, descripcion?: string }} data
 */
export const actualizarAreaEmpresa = async (id, data) => {
  const response = await api.put(`areas/${id}/`, data)
  return response.data
}

/**
 * Borrado lógico de un área (activo = false).
 * @param {number} id
 */
export const eliminarAreaEmpresa = async (id) => {
  const response = await api.delete(`areas/${id}/`)
  return response.data
}

export const getMaquinaPublica = async (id) => {
  const response = await api.get(`maquinas/${id}/public/`)
  return response.data
}

/**
 * Obtiene calendarios del mes agrupados por (máquina, fecha).
 * Cada grupo contiene todos los CalendarioMantenimiento de esa máquina en esa fecha.
 * @param {{ mes: string, maquina?: number, periodicidad?: string }} params
 */
export const getCalendariosAgrupados = async (params = {}) => {
  const response = await api.get('calendarios/agrupados/', { params })
  return response.data
}
 
/**
 * Crea múltiples CalendarioMantenimiento en un solo request.
 * @param {{
 *   maquina:          number,
 *   rutinas:          number[],
 *   fecha_programada: string    // YYYY-MM-DD
 * }} data
 * @returns {{
 *   creados:  object[],   // CalendarioMantenimiento serializados
 *   omitidos: number[],   // rutina IDs ya programadas ese día
 *   errores:  object[]    // { rutina, motivo }
 * }}
 */
export const crearCalendariosBulk = async (data) => {
  const response = await api.post('calendarios/bulk/', data)
  return response.data
}

export function agregarProyectoPedido(numeroPedido, formData) {
  return api.post(`/pedidos/${numeroPedido}/proyectos/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function eliminarProyectoPedido(numeroPedido, proyectoId) {
  return api.delete(`/pedidos/${numeroPedido}/proyectos/${proyectoId}/`)
}


// ── Catálogo de máquinas (Divisan - producción) ─────────────
export const getCatalogoMaquinas      = () => api.get('catalogo-maquinas/').then(r => r.data)
export const crearCatalogoMaquina     = (data) => api.post('catalogo-maquinas/', data).then(r => r.data)
export const actualizarCatalogoMaquina = (id, data) => api.put(`catalogo-maquinas/${id}/`, data).then(r => r.data)
export const eliminarCatalogoMaquina  = (id) => api.delete(`catalogo-maquinas/${id}/`).then(r => r.data)

// ── Operadores ──────────────────────────────────────────────
export const getOperadores      = () => api.get('operadores/').then(r => r.data)
export const crearOperador      = (data) => api.post('operadores/', data).then(r => r.data)
export const actualizarOperador = (id, data) => api.put(`operadores/${id}/`, data).then(r => r.data)
export const eliminarOperador   = (id) => api.delete(`operadores/${id}/`).then(r => r.data)

// ── Pigmentos ───────────────────────────────────────────────
export const getPigmentos      = () => api.get('colores/').then(r => r.data)
export const crearPigmento      = (data) => api.post('colores/', data).then(r => r.data)
export const actualizarPigmento = (id, data) => api.put(`colores/${id}/`, data).then(r => r.data)
export const eliminarPigmento   = (id) => api.delete(`colores/${id}/`).then(r => r.data)

export const subirFichaTecnicaPigmento = (pigmentoId, formData) =>
  api.post(`colores/${pigmentoId}/fichas-tecnicas/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data)

export const eliminarFichaTecnicaPigmento = (fichaId) =>
  api.delete(`colores/fichas-tecnicas/${fichaId}/`).then(r => r.data)

// ── TPUs ────────────────────────────────────────────────────
export const getTPUs      = () => api.get('tpu/').then(r => r.data)
export const crearTPU     = (data) => api.post('tpu/', data).then(r => r.data)
export const actualizarTPU = (id, data) => api.put(`tpu/${id}/`, data).then(r => r.data)
export const eliminarTPU  = (id) => api.delete(`tpu/${id}/`).then(r => r.data)

// ── Códigos de defecto ──────────────────────────────────────
export const getCodigosDefecto      = () => api.get('codigos-defecto/').then(r => r.data)
export const crearCodigoDefecto     = (data) => api.post('codigos-defecto/', data).then(r => r.data)
export const actualizarCodigoDefecto = (id, data) => api.put(`codigos-defecto/${id}/`, data).then(r => r.data)
export const eliminarCodigoDefecto  = (id) => api.delete(`codigos-defecto/${id}/`).then(r => r.data)

export const subirImagenCodigoDefecto = (defectoId, formData) =>
  api.post(`codigos-defecto/${defectoId}/imagenes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data)

export const eliminarImagenCodigoDefecto = (imagenId) =>
  api.delete(`codigos-defecto/imagenes/${imagenId}/`).then(r => r.data)

// ── TPU — Documentos ────────────────────────────────────────────
export const subirFichaTecnicaTPU = (tpuId, formData) =>
  api.post(`tpu/${tpuId}/fichas-tecnicas/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data)

export const eliminarFichaTecnicaTPU = (fichaId) =>
  api.delete(`tpu/fichas-tecnicas/${fichaId}/`).then(r => r.data)

export const subirHojaSeguridadTPU = (tpuId, formData) =>
  api.post(`tpu/${tpuId}/hojas-seguridad/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data)

export const eliminarHojaSeguridadTPU = (hojaId) =>
  api.delete(`tpu/hojas-seguridad/${hojaId}/`).then(r => r.data)

// ── Catálogo de Productos (Divisan - producción) ────────────
// Solo queda el listado: alimenta el filtro de producto del dashboard de
// Scrap Almacén y Láser. El CRUD (crear/actualizar/eliminar) se retiró
// junto con CrudCatProductos.vue — CatProductos ya no se administra desde
// aquí, solo se conserva para no romper los formularios públicos mientras
// se termina de migrar todo al nuevo modelo Producto.
export const getCatalogoProductos = () => api.get('catalogo-productos/').then(r => r.data)

// ==========================================
// MÓDULO DE INYECCIÓN — Reportes de producción
// ==========================================
export const getReportesProduccion = (params = {}) =>
  api.get('inyeccion/reportes/', { params }).then(r => r.data)

export const getResumenReportesProduccion = (params = {}) =>
  api.get('inyeccion/reportes/resumen/', { params }).then(r => r.data)

export const crearReporteProduccion = (data) =>
  api.post('inyeccion/reportes/', data).then(r => r.data)

export const actualizarReporteProduccion = (id, data) =>
  api.put(`inyeccion/reportes/${id}/`, data).then(r => r.data)

export const cancelarReporteProduccion = (id) =>
  api.delete(`inyeccion/reportes/${id}/`).then(r => r.data)

export const actualizarLineaReporteProduccion = (reporteId, lineaId, data) =>
  api.patch(`inyeccion/reportes/${reporteId}/lineas/${lineaId}/`, data).then(r => r.data)

export const eliminarLineaReporteProduccion = (reporteId, lineaId) =>
  api.delete(`inyeccion/reportes/${reporteId}/lineas/${lineaId}/`).then(r => r.data)

export const enviarReportesAprobacion = (ids) =>
  api.post('inyeccion/reportes/enviar-aprobacion/', { ids }).then(r => r.data)

// ── Validación de Almacén (RN: validación Taller→Almacén) ──
export const capturarCantidadRecibida = (reporteId, lineaId, cantidadRecibida) =>
  api.patch(`inyeccion/reportes/${reporteId}/lineas/${lineaId}/recibido/`, { cantidad_recibida: cantidadRecibida }).then(r => r.data)

export const justificarDiferenciaLinea = (reporteId, lineaId, { motivo, observaciones }) =>
  api.post(`inyeccion/reportes/${reporteId}/lineas/${lineaId}/justificar-diferencia/`, { motivo, observaciones }).then(r => r.data)

export const confirmarRecepcionReporte = (reporteId) =>
  api.post(`inyeccion/reportes/${reporteId}/confirmar-recepcion/`).then(r => r.data)

export const exportarReportesProduccion = async (params = {}) => {
  const response = await api.get('inyeccion/reportes/exportar/', { params, responseType: 'blob' })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'reportes_produccion.xlsx')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}